import { FC } from 'react';

import {
  LoadingOutlined,
  UserOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  LockOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Spin, Input } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { T } from 'react-translator-component';
import { Dispatch } from 'redux';

import * as S from './styled';
import TwoFactorVerification from './TwoFactorVerification';
import { useLogin } from './useLogin';

import { useTheme } from '../../../../hooks/useTheme';
import { Shell } from '../../../atoms/Shell/Shell';
import { CelescLogos } from '../CelescLogos';

export type LoginPageProps = {
  dispatch: Dispatch;
};

const LoginPage: FC<LoginPageProps> = ({ dispatch }) => {
  const {
    captchaRef,
    handleFormSubmit,
    isLoginLoading,
    isVerified,
    recaptchaExpired,
    recaptchaLoaded,
    twoFactorData,
    handleTwoFactorSuccess,
    handleBackToLogin,
  } = useLogin({ dispatch });

  const { theme } = useTheme();
  const host = window.location.hostname;

  const showButtonCreateAccount =
    host === 'ds-intelbras.use-move.com' ||
    host === 'ds-test-intelbras.use-move.com' ||
    host === 'ds-intelbras-cve-pro.com.br' ||
    host === 'intelbras-cve-pro.com.br';

  if (twoFactorData) {
    return (
      <S.LoginFormPageWrapper>
        {theme ? (
          <img
            src={`https://storage.googleapis.com/mk-move-repository/white-label/${theme}/images/dash-logo.png`}
            className={`${theme}-logo-auth`}
          />
        ) : (
          <Shell height={50} width={200} />
        )}

        <TwoFactorVerification
          email={twoFactorData.email}
          maskedEmail={twoFactorData.maskedEmail}
          onSuccess={handleTwoFactorSuccess}
          onBackToLogin={handleBackToLogin}
        />
      </S.LoginFormPageWrapper>
    );
  }

  return (
    <S.LoginFormPageWrapper>
      {theme ? (
        <img
          src={`https://storage.googleapis.com/mk-move-repository/white-label/${theme}/images/dash-logo.png`}
          className={`${theme}-logo-auth`}
        />
      ) : (
        <Shell height={50} width={200} />
      )}

      <S.LoginFormWrapper>
        <form onSubmit={handleFormSubmit}>
          <S.LoginFormInputWrapper>
            {theme ? (
              <Input
                className='input'
                id='email'
                type='email'
                size='large'
                placeholder='Usuário'
                prefix={<UserOutlined style={{ color: '#c1c6cb' }} />}
              />
            ) : (
              <Shell height={40} width={304} />
            )}
          </S.LoginFormInputWrapper>

          <S.LoginFormInputWrapper>
            {theme ? (
              <Input.Password
                className='input'
                id='password'
                type='password'
                size='large'
                prefix={<LockOutlined style={{ color: '#c1c6cb' }} />}
                placeholder={T('Password')}
                iconRender={(visible) =>
                  visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            ) : (
              <Shell height={40} width={304} />
            )}
          </S.LoginFormInputWrapper>

          <S.ReCaptchaWrapper>
            {theme ? (
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                render='explicit'
                onChange={recaptchaLoaded}
                onExpired={recaptchaExpired}
                ref={captchaRef}
                size='normal'
              />
            ) : (
              <Shell height={78} width={304} />
            )}
          </S.ReCaptchaWrapper>

          {isLoginLoading ? (
            <Spin
              style={{ display: 'flex', justifyContent: 'center' }}
              indicator={
                <LoadingOutlined style={{ fontSize: 40, margin: 10 }} spin />
              }
            />
          ) : (
            <S.LoginFormButtonWrapper>
              {theme ? (
                <S.LoginFormButton type='submit' disabled={!isVerified}>
                  {T('Enter')}
                </S.LoginFormButton>
              ) : (
                <Shell height={53} width={304} />
              )}
            </S.LoginFormButtonWrapper>
          )}

          <S.RegisterText>
            {theme ? (
              <Link to='/auth/recover'>{T('Recover password')}</Link>
            ) : (
              <Shell height={22} width={105} />
            )}
          </S.RegisterText>
        </form>
      </S.LoginFormWrapper>

      {showButtonCreateAccount && (
        <>
          <S.Separator />

          <S.LoginFormButtonWrapper>
            {theme ? (
              <S.CreatedAccount as={Link} to='/auth/createaccount'>
                <SettingOutlined height={22} width={50} />
                {T('Create account')}
              </S.CreatedAccount>
            ) : (
              <Shell height={22} width={200} />
            )}
          </S.LoginFormButtonWrapper>
        </>
      )}

      {theme === 'celesc' && <CelescLogos />}
    </S.LoginFormPageWrapper>
  );
};

const mapStateToProps = (state) => ({ user: state.user });

export default connect(mapStateToProps)(LoginPage);
